import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useProperties } from "../context/PropertyContext";
import PropertySelector from "../components/PropertySelector";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, ClipboardList, Phone, Building2, User, Wallet, Shield, Trash2, FileText } from "lucide-react";

const STATUSES = ["all", "rented", "reserved"];

export default function Bookings() {
  const { t } = useLang();
  const { format: money, code: ccy } = useCurrency();
  const { properties, currentPropertyId } = useProperties();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (currentPropertyId) params.property_id = currentPropertyId;
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.q = search;
      const { data } = await api.get("/bookings", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentPropertyId, statusFilter]);

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const remove = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success(t("deleted"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totals = useMemo(() => {
    const income = rows.filter((r) => r.status === "rented").reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const deposit = rows.reduce((s, r) => s + (Number(r.deposit) || 0), 0);
    const insurance = rows.reduce((s, r) => s + (Number(r.insurance) || 0), 0);
    return { income, deposit, insurance, count: rows.length };
  }, [rows]);

  const propertyName = (pid) => properties.find((p) => p.id === pid)?.name || "—";

  const exportCsv = () => {
    const rowsCsv = [
      ["Date", "Property", "Status", "Guest name", "Phone", `Amount (${ccy})`, "Deposit", "Insurance", "Notes"],
      ...rows.map((r) => [
        r.date, propertyName(r.property_id), r.status, r.guest_name || "", r.guest_phone || "",
        r.amount || 0, r.deposit || 0, r.insurance || 0, (r.notes || "").replace(/\n/g, " "),
      ]),
    ];
    const csv = "\ufeff" + rowsCsv.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aqar-bookings.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const statusLabel = { all: t("filter_all"), rented: t("status_rented"), reserved: t("status_reserved") };

  return (
    <div className="space-y-5" data-testid="bookings-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
            <ClipboardList className="text-[#2A5948]" size={26} />
            {t("bookings_history")}
          </h1>
          <p className="text-sm text-[#5C6053] mt-1">{t("bookings_history_desc")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {properties.length > 1 && <PropertySelector />}
          <button data-testid="bookings-export-btn" onClick={exportCsv} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E1D8] bg-white text-sm hover:bg-[#F3EFE7]">
            <FileText size={14} /> {t("export_csv")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat testid="bookings-count" label={t("total_bookings")} value={totals.count} tone="brand" />
        <MiniStat testid="bookings-income" label={t("income")} value={money(totals.income)} tone="green" />
        <MiniStat testid="bookings-deposits" label={t("deposit")} value={money(totals.deposit)} tone="accent" />
        <MiniStat testid="bookings-insurance" label={t("insurance")} value={money(totals.insurance)} tone="amber" />
      </div>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-3 shadow-sm flex flex-wrap gap-2 items-center">
        <form onSubmit={onSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-1 border border-[#E5E1D8] bg-[#F9F6F0] rounded-lg px-3 py-2">
            <Search size={14} className="text-[#5C6053]" />
            <input
              data-testid="bookings-search-input"
              placeholder={t("search_guest")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent outline-none text-sm flex-1"
            />
          </div>
          <button data-testid="bookings-search-btn" type="submit" className="brand-btn px-3 py-2 rounded-lg text-sm">{t("search")}</button>
        </form>
        <div className="flex gap-1 p-1 bg-[#F9F6F0] rounded-lg border border-[#E5E1D8]">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`bk-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-md ${statusFilter === s ? "bg-[#2A5948] text-white" : "text-[#1C1E17]"}`}
            >
              {statusLabel[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase">
            <tr>
              <th className="text-start px-4 py-3">{t("date")}</th>
              <th className="text-start px-4 py-3">{t("property")}</th>
              <th className="text-start px-4 py-3">{t("guest_name")}</th>
              <th className="text-start px-4 py-3">{t("guest_phone")}</th>
              <th className="text-start px-4 py-3">{t("amount")}</th>
              <th className="text-start px-4 py-3">{t("deposit")}</th>
              <th className="text-start px-4 py-3">{t("insurance")}</th>
              <th className="text-start px-4 py-3">{t("notes")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[#5C6053]">—</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`booking-row-${r.id}`} className="border-t border-[#E5E1D8] hover:bg-[#F9F6F0]">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="font-medium">{r.date}</div>
                  <StatusBadge status={r.status} t={t} />
                </td>
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-1 text-xs text-[#5C6053]">
                    <Building2 size={12} /> {propertyName(r.property_id)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-1"><User size={12} className="text-[#5C6053]" />{r.guest_name || "—"}</div>
                </td>
                <td className="px-4 py-3" dir="ltr">
                  {r.guest_phone ? (
                    <a href={`tel:${r.guest_phone}`} data-testid={`booking-call-${r.id}`} className="inline-flex items-center gap-1 text-[#2A5948] hover:underline">
                      <Phone size={12} /> {r.guest_phone}
                    </a>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="inline-flex items-center gap-1"><Wallet size={12} className="text-[#5C6053]" />{money(r.amount || 0)}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{money(r.deposit || 0)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="inline-flex items-center gap-1"><Shield size={12} className="text-[#5C6053]" />{money(r.insurance || 0)}</div>
                </td>
                <td className="px-4 py-3 text-xs text-[#5C6053] max-w-[220px] truncate">{r.notes || ""}</td>
                <td className="px-4 py-3 text-end">
                  <button data-testid={`booking-delete-${r.id}`} onClick={() => remove(r.id)} className="text-[#C96A52] hover:bg-[#FBE9E4] p-2 rounded">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    rented: "bg-[#E6F1E7] text-[#2A5948]",
    reserved: "bg-[#FFF3D6] text-[#8A6D1F]",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded ${map[status] || "bg-[#F3EFE7] text-[#5C6053]"}`}>{t(`status_${status}`)}</span>;
}

function MiniStat({ label, value, tone, testid }) {
  const map = {
    brand: "bg-[#2A5948] text-white",
    green: "bg-[#E6F1E7] text-[#2A5948]",
    accent: "bg-[#F3EFE7] text-[#2A5948]",
    amber: "bg-[#FFF3D6] text-[#8A6D1F]",
  };
  return (
    <div data-testid={testid} className="rounded-xl border border-[#E5E1D8] bg-white p-3 shadow-sm">
      <div className={`inline-block text-[10px] px-2 py-0.5 rounded ${map[tone]}`}>{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
